import FormFooter from "../../components/CustomForms/FormFooter";
import CustomDatePicker from "../../components/CustomDatePicker/CustomDatePicker";
import CustomTimePicker from "../../components/CustomTimePicker/CustomTimePicker";
import CustomInput from "../../components/CustomInput/Input";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import { ERROR_MSGS, MEETING_PLATFORMS } from "../../constants/constants";
import { useEffect, useState } from "react";
import { businessSummaryRules, handleUploadFile } from "../../helpers/helpers";
import { Form, Row, Col, Upload, Typography, App, Button } from "antd";
import {
  UploadOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import CustomButton from "../../components/CustomButton/Button";
import { useDispatch, useSelector } from "react-redux";
import {
  sendPitchEmail,
  updateErrorStatus,
} from "../../redux/slices/DealApprovalSlice";
import dayjs from "dayjs";

const { Text } = Typography;

const MeetingEmailForm = ({
  investors,
  onClose,
  dealId,
  activeTab,
  onSuccess,
}) => {
  const { message } = App.useApp();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [fileUrl, setFileUrl] = useState(null);
  const { pitchEmailError, pitchEmailLoading } = useSelector(
    (state) => state.dealApproval || {},
  );
  const meetingDate = Form.useWatch("meetingDate", form);
  const startTime = Form.useWatch("startTime", form);

  useEffect(() => {
    return () => {
      dispatch(updateErrorStatus());
    };
  }, [dispatch]);

  const getDisabledTime = () => {
    if (!meetingDate || !meetingDate.isSame(dayjs(), "day")) {
      return {};
    }

    const now = dayjs();

    return {
      disabledHours: () => Array.from({ length: now.hour() }, (_, i) => i),

      disabledMinutes: (selectedHour) => {
        if (selectedHour === now.hour()) {
          return Array.from({ length: now.minute() }, (_, i) => i);
        }
        return [];
      },
    };
  };

  const getEndDisabledTime = () => {
    if (!startTime) return {};

    return {
      disabledHours: () =>
        Array.from({ length: startTime.hour() }, (_, i) => i),

      disabledMinutes: (selectedHour) => {
        if (selectedHour === startTime.hour()) {
          return Array.from({ length: startTime.minute() + 1 }, (_, i) => i);
        }
        return [];
      },
    };
  };

  const handleSubmit = async (values) => {
    const payload = {
      dealId,
      orgId: investors.map((i) => i.orgId),
      meetingDate: values.meetingDate.format("DD MMM' YYYY"),
      meetingTime: `${values.startTime.format("hh:mm A")} - ${values.endTime.format("hh:mm A")}`,
      meetingPlatform: values.meetingPlatform,
      meetingLink: values.meetingLink,
      pitchImageUrl: fileUrl,
      summary: values.summary,
      highlights: values.highlights ?? [],
      keyNumbers: values.keyNumbers ?? [],
      interestStatus: activeTab,
    };

    const res = await dispatch(sendPitchEmail(payload));

    if (res?.payload?.statusCode === 201) {
      message.success("Pitch Email Sent Successfully");
      form.resetFields();
      setFileUrl(null);
      onSuccess?.();
    }
  };

  const handleBeforeUpload = (file) => {
    const allowedTypes = ["png", "jpg", "jpeg"];
    const fileType = file.name.split(".").pop().toLowerCase();
    const isAllowed = allowedTypes.includes(fileType);

    if (!isAllowed) {
      message.error(`Only image files are allowed`);
      return Upload.LIST_IGNORE;
    }

    const isLt5MB = file.size / 1024 / 1024 < 5;
    if (!isLt5MB) {
      message.error("File must be smaller than 5MB!");
      return Upload.LIST_IGNORE;
    }

    return true; // Allow upload
  };

  const handleCustomRequest = async ({ file, onSuccess, onError }) => {
    const cleanName = file.name.replace(/\s+/g, "_");
    const cleanFile = new File([file], cleanName, { type: file.type });

    try {
      const path = `business`;
      const { fileUrl } = await handleUploadFile(cleanFile, path);
      setFileUrl(fileUrl);
      message.success("File uploaded successfully");
      onSuccess("ok");
    } catch (err) {
      onError(err);
    }
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".png,,jpg,.jpeg",
    maxCount: 1,
    beforeUpload: handleBeforeUpload,
    customRequest: handleCustomRequest,
  };

  const handleMeetingDateChange = () => {
    form.setFieldsValue({
      startTime: null,
      endTime: null,
    });
  };

  const handleStartTimeChange = () => {
    form.setFieldsValue({
      endTime: null,
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      className="pitch-email-form"
    >
      <Row gutter={[24, 0]}>
        <Col xs={24} md={12}>
          <CustomDatePicker
            label="Meeting Date"
            name="meetingDate"
            isRequired={true}
            placeholder="Select Meeting Date"
            onChange={handleMeetingDateChange}
            rules={[{ required: true, message: ERROR_MSGS.MEET_DATE_ERR_MSG }]}
            disabledDate={(current) =>
              current && current.isBefore(dayjs(), "day")
            }
          />
        </Col>
        <Col xs={24} md={12}>
          <CustomTimePicker
            label="Start Time"
            name="startTime"
            isRequired
            disabled={!meetingDate}
            disabledTime={getDisabledTime}
            onChange={handleStartTimeChange}
            rules={[{ required: true }]}
          />
        </Col>

        <Col xs={24} md={12}>
          <CustomTimePicker
            label="End Time"
            name="endTime"
            isRequired
            disabled={!startTime}
            disabledTime={getEndDisabledTime}
            rules={[
              { required: true },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const startTime = getFieldValue("startTime");

                  if (!startTime || !value || value.isAfter(startTime)) {
                    return Promise.resolve();
                  }

                  return Promise.reject(
                    new Error("End time must be after start time"),
                  );
                },
              }),
            ]}
          />
        </Col>

        <Col xs={24} md={12}>
          <CustomSelect
            label="Meeting Platform"
            name="meetingPlatform"
            isRequired
            placeholder="Select Meeting Platform"
            options={MEETING_PLATFORMS}
            rules={[
              {
                required: true,
                message: ERROR_MSGS.MEETING_PLATFORM_ERR_MSG,
              },
            ]}
          />
        </Col>
        <Col xs={24} md={12}>
          <CustomInput
            label="Meeting Link"
            name="meetingLink"
            isRequired={true}
            placeholder="https://meet.google.com/..."
            rules={[
              { required: true },
              { type: "url", message: "Please enter a valid URL" },
            ]}
          />
        </Col>
        <Col xs={24}>
          <Form.Item
            label={
              <Text>
                Upload Business Pitch Image <Text type="danger"> *</Text>
              </Text>
            }
            name="pitchImg"
            valuePropName="fileList"
            getValueFromEvent={(e) => e?.fileList}
            rules={[
              { required: true, message: "Please upload Business Pitch Image" },
            ]}
            className="form-item-class"
          >
            <Upload.Dragger {...uploadProps} className="question-bank-uploader">
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="question-bank-upload-title">
                Click to upload Image
              </p>
              <p className="question-bank-upload-subtitle">
                {`Accepted file types: .png,.jpg,.jpeg. Max file size: 5MB.`}
              </p>
            </Upload.Dragger>
          </Form.Item>
        </Col>
        <Col xs={24}>
          <CustomInput
            label="Business Summary"
            name="summary"
            isRequired
            isTextArea
            placeholder="Enter a brief overview of the business..."
            rows={6}
            rules={businessSummaryRules}
          />
        </Col>
        <Col xs={24}>
          <Typography.Title level={5}>
            Highlights <Text type="danger"> *</Text>
          </Typography.Title>

          <Form.List
            name="highlights"
            rules={[
              {
                validator: async (_, highlights) => {
                  if (!highlights || highlights.length === 0) {
                    return Promise.reject(
                      new Error("Please add at least one highlight"),
                    );
                  }
                },
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map(({ key, name }) => (
                  <Row key={key} gutter={8} align="top">
                    <Col flex="auto">
                      <CustomInput
                        name={name}
                        placeholder="Enter a highlight"
                        rules={[
                          {
                            required: true,
                            message: "Please enter a highlight",
                          },
                          {
                            validator: (_, value) => {
                              if (!value || value.length <= 100) {
                                return Promise.resolve();
                              }

                              return Promise.reject(
                                new Error(
                                  "Highlight cannot exceed 100 characters",
                                ),
                              );
                            },
                          },
                        ]}
                      />
                    </Col>

                    <Col>
                      <CustomButton
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      />
                    </Col>
                  </Row>
                ))}

                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  style={{ marginBottom: 10 }}
                  onClick={() => add()}
                >
                  Add Highlight
                </Button>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>
        </Col>
        <Col xs={24}>
          <Typography.Title level={5}>
            Key Numbers <Text type="danger"> *</Text>
          </Typography.Title>

          <Form.List
            name="keyNumbers"
            rules={[
              {
                validator: async (_, keyNumbers) => {
                  if (!keyNumbers || keyNumbers.length === 0) {
                    return Promise.reject(
                      new Error("Please add at least one key number"),
                    );
                  }
                },
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map(({ key, name }) => (
                  <Row key={key} gutter={8} align="top">
                    <Col flex="auto">
                      <CustomInput
                        name={name}
                        placeholder="Enter a key number"
                        rules={[
                          {
                            required: true,
                            message: "Please enter a key number",
                          },
                          {
                            validator: (_, value) => {
                              if (!value || value.length <= 100) {
                                return Promise.resolve();
                              }

                              return Promise.reject(
                                new Error(
                                  "Key number cannot exceed 100 characters",
                                ),
                              );
                            },
                          },
                        ]}
                      />
                    </Col>

                    <Col>
                      <CustomButton
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      />
                    </Col>
                  </Row>
                ))}

                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => add()}
                >
                  Add Key Number
                </Button>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>
        </Col>
      </Row>

      <FormFooter
        onCancelClick={() => {
          onClose();
          form.resetFields();
        }}
        error={pitchEmailError}
        submitLoading={pitchEmailLoading}
        btnName="Send Email"
      />
    </Form>
  );
};

export default MeetingEmailForm;
